import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { Alert, Intent } from '@blueprintjs/core';
import Pagination from 'components/Pagination';
import Footer from 'components/Footer';
import Toast from 'helpers/toaster';
import * as DataList from 'components/DataList';

// Layout shared by the list and its loading skeleton (pages/questions/index.js).
export const QUESTION_COLUMNS = [
  { area: 'label', label: 'Field label', width: 'minmax(200px, 1.6fr)', bone: 'single' },
  { area: 'type', label: 'Field type', width: 'minmax(110px, 0.7fr)', bone: 'single', icon: 'form' },
  { area: 'service', label: 'Service type', width: 'minmax(150px, 1fr)', bone: 'single', icon: 'tag' },
  { area: 'group', label: 'Question group', width: 'minmax(150px, 1fr)', bone: 'single', icon: 'folder-close' },
  { area: 'actions', label: '', width: '72px', bone: 'none' },
];

export const QUESTION_CARDS = {
  columns: 'minmax(0, 1fr) auto',
  areas: ['label actions', 'type type', 'service service', 'group group'],
};

export const QUESTION_MIN_TABLE_WIDTH = 840;

// "text_area" -> "Text area"
const humanize = (value) => {
  const text = String(value).replace(/[_-]+/g, ' ').trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
};

@inject('store', 'clientStore', 'questionStore', 'viewStore') @observer
class QuestionsTable extends Component {
  @observable isOpenAddEquipmentDialog = false;
  @observable isDeleteDialogVisible = false;
  @observable question;

  onPaginationChange(newPage) {
    let {
      viewStore: {
        questionSearch
      }
    } = this.props;
    this.props.viewStore.questionSearch = {
      ...questionSearch,
      page: newPage
    };
    this.props.viewStore.questions();
  }

  handleDeleteCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleDeleteConfirm() {
    this.isDeleteDialogVisible = false;
    this.props.questionStore.deleteQuestion(this.question)
      .then(this.onSuccessfulDeleteQuestion.bind(this))
      .catch(this.onFailDeleteQuestion.bind(this))
  }

  onSuccessfulDeleteQuestion(response) {
    Toast.show({ icon: 'thumbs-up', message: 'Question deleted successfully!', intent: Intent.SUCCESS })
    this.service = null;
    this.isDeleteDialogVisible = false;
  }

  onFailDeleteQuestion(error) {
    this.isDeleteDialogVisible = false;
    Toast.show({ icon: 'warning-sign', message: 'Unable to delete question!', intent: Intent.DANGER })
  }

  handleServiceTypeClick(event, question) {
    switch (event.label) {
      case 'Edit':
        this.question = question;
        this.props.viewStore.questionShow({uuid: question.attributes.id,name:question.attributes.field_label})
        break;
      case 'Delete':
        this.isDeleteDialogVisible = true;
        this.question = question;
        break;
      default:
    }
  }

  // Empty list's "Clear search": back to all questions, page 1.
  clearSearch() {
    const { viewStore } = this.props;
    viewStore.questionSearch = { ...viewStore.questionSearch, term: '*', page: 1 };
    viewStore.bumpListFilters();
    viewStore.questions();
  }

  render() {
    let {
      viewStore,
      questionStore: {
        questions: {
          objects,
          meta
        }
      }
    } = this.props;
    const { questionSearch } = viewStore;
    const term = questionSearch.term && questionSearch.term !== '*' ? questionSearch.term : '';

    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="questions"
          term={term}
          onClearFilters={this.clearSearch.bind(this)}
          extraAction={!term && { label: 'Add question', icon: 'plus', onClick: () => viewStore.questionAdd(), primary: true }}
        />
      );
    }
    return (
      <React.Fragment>
        <Footer>
          <div className="bp3-navbar-group bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
        <DataList.List
          columns={QUESTION_COLUMNS}
          cards={QUESTION_CARDS}
          minTableWidth={QUESTION_MIN_TABLE_WIDTH}
          total={meta.total_records || objects.length}
          noun="questions"
          note={DataList.sortNote(questionSearch.order)}
        >
          {objects.map((questions) => {
            const attributes = questions.attributes;
            const serviceType = attributes.service_type && attributes.service_type.name;
            const group = attributes.question_group_id && attributes.question_group && attributes.question_group.name;
            return (
              <DataList.Row key={questions.id}>
                <DataList.Cell area="label">
                  <DataList.Lines
                    title={attributes.field_label}
                    primary={
                      <DataList.RowLink
                        href={`/dashboard/question/${attributes.id}/show`}
                        label={`Question ${attributes.field_label}`}
                      >
                        {attributes.field_label}
                      </DataList.RowLink>
                    }
                  />
                </DataList.Cell>
                <DataList.Cell area="type">
                  {attributes.field_type ? (
                    <DataList.Lines primary={humanize(attributes.field_type)} strong={false} />
                  ) : (
                    <DataList.Muted>—</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="service">
                  {serviceType ? (
                    <DataList.Lines primary={serviceType} strong={false} />
                  ) : (
                    <DataList.Muted>No service type</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="group">
                  {group ? (
                    <DataList.Lines primary={group} strong={false} />
                  ) : (
                    <DataList.Muted>No group</DataList.Muted>
                  )}
                </DataList.Cell>
                <DataList.Cell area="actions">
                  <DataList.Actions
                    items={[
                      { icon: 'edit', label: 'Edit question', onClick: () => viewStore.questionShow({ uuid: attributes.id, name: attributes.field_label }) },
                      { icon: 'trash', label: 'Delete question', intent: 'danger', onClick: () => this.handleServiceTypeClick({ label: 'Delete' }, questions) },
                    ]}
                  />
                </DataList.Cell>
              </DataList.Row>
            );
          })}
        </DataList.List>
        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleDeleteCancel.bind(this)}
          onConfirm={this.handleDeleteConfirm.bind(this)}
        >
          <p>Warnning! Deleting this question will not be recoverable.</p>
          <p>Are you sure you want to do this?</p>
        </Alert>
        <Footer>
          <div className="bp3-navbar-group bp3-align-left">
            <Pagination
              value={meta.page}
              total={meta.total_pages}
              onChange={this.onPaginationChange.bind(this)}
            />
          </div>
        </Footer>
      </React.Fragment>
    )
  }

}

export default QuestionsTable;